/**
 * S11 — THE CANON (the full reference library)
 *
 * Clickable lightbox gallery surfacing the curated moodboard, filtered
 * to exclude off-brand references (Alfredo album + surreal/retro-tech
 * cluster). Placed after S10 La Famiglia and before S09 End Card.
 */

import { useEffect, useState, useCallback } from "react";
import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy } from "@/lib/voice";
import { canonImages, moodboardUrl, moodboardLargeUrl } from "@/lib/moodboard";

export function S11_TheCanon() {
  const copy = sectionCopy.s11;
  // Active position in the canonImages array (not idx — position)
  const [activePos, setActivePos] = useState<number | null>(null);

  const close = useCallback(() => setActivePos(null), []);
  const next = useCallback(() => {
    setActivePos((curr) => {
      if (curr === null) return null;
      return (curr + 1) % canonImages.length;
    });
  }, []);
  const prev = useCallback(() => {
    setActivePos((curr) => {
      if (curr === null) return null;
      return (curr - 1 + canonImages.length) % canonImages.length;
    });
  }, []);

  useEffect(() => {
    if (activePos === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [activePos, close, next, prev]);

  const activeImage = activePos !== null ? canonImages[activePos] : null;

  return (
    <section
      id="s11"
      className="section"
      style={{
        backgroundColor: "var(--oil)",
        color: "var(--bone)",
        flexDirection: "column",
        padding: "12vh var(--space-lg)",
        minHeight: "auto",
      }}
    >
      <MonumentType word={copy.monument} theme="ink" />

      <div
        style={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          marginBottom: "var(--space-xl)",
        }}
      >
        <div
          className="type-meta-sm"
          style={{
            color: "var(--loden)",
            marginBottom: "var(--space-md)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {copy.meta} — REFERENCE LIBRARY · {canonImages.length} FRAMES
        </div>
        <h2
          className="type-hero-lg"
          style={{ color: "var(--bone)", margin: 0 }}
        >
          {copy.hero}
        </h2>
        {copy.subhero && (
          <p
            className="type-voice"
            style={{
              color: "var(--fog)",
              marginTop: "var(--space-md)",
              fontSize: "clamp(1.125rem, 1.5vw, 1.5rem)",
              fontStyle: "italic",
            }}
          >
            {copy.subhero}
          </p>
        )}
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 3,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: "var(--space-xs)",
          width: "min(1600px, 92vw)",
          margin: "0 auto",
        }}
      >
        {canonImages.map((img, pos) => (
          <button
            key={img.idx}
            onClick={() => setActivePos(pos)}
            aria-label={img.alt || `Frame ${img.idx}`}
            style={{
              position: "relative",
              aspectRatio: "1",
              overflow: "hidden",
              cursor: "pointer",
              padding: 0,
              border: "1px solid rgba(245,240,228,0.08)",
              background: "transparent",
              transition: "transform 0.3s var(--ease-cinematic), border-color 0.3s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.04)";
              e.currentTarget.style.borderColor = "var(--loden)";
              e.currentTarget.style.zIndex = "2";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.borderColor = "rgba(245,240,228,0.08)";
              e.currentTarget.style.zIndex = "1";
            }}
          >
            <img
              src={moodboardUrl(img.idx)}
              alt=""
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "grayscale(40%) contrast(1.1) brightness(0.85)",
                transition: "filter 0.3s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLImageElement).style.filter =
                  "grayscale(0%) contrast(1.15) brightness(1)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLImageElement).style.filter =
                  "grayscale(40%) contrast(1.1) brightness(0.85)";
              }}
            />
            <span
              className="type-meta-sm"
              style={{
                position: "absolute",
                bottom: 4,
                left: 6,
                color: "var(--fog)",
                fontSize: "9px",
                opacity: 0.7,
                pointerEvents: "none",
              }}
            >
              {String(img.idx).padStart(3, "0")}
            </span>
          </button>
        ))}
      </div>

      {/* Lightbox overlay */}
      {activeImage && activePos !== null && (
        <div
          role="dialog"
          aria-label="Image preview"
          onClick={close}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(5,5,5,0.96)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "var(--space-lg)",
            cursor: "pointer",
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            className="type-meta-sm"
            style={{
              position: "absolute",
              top: "var(--space-lg)",
              right: "var(--space-lg)",
              color: "var(--bone)",
              background: "transparent",
              border: "1px solid var(--bone)",
              padding: "var(--space-xs) var(--space-md)",
              cursor: "pointer",
              letterSpacing: "var(--ls-meta-wide)",
            }}
          >
            CLOSE ✕
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Previous"
            style={{
              position: "absolute",
              left: "var(--space-lg)",
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--bone)",
              background: "transparent",
              border: "1px solid var(--bone)",
              padding: "var(--space-md)",
              cursor: "pointer",
              fontSize: "1.5rem",
              fontFamily: "var(--f-mono)",
            }}
          >
            ←
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Next"
            style={{
              position: "absolute",
              right: "var(--space-lg)",
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--bone)",
              background: "transparent",
              border: "1px solid var(--bone)",
              padding: "var(--space-md)",
              cursor: "pointer",
              fontSize: "1.5rem",
              fontFamily: "var(--f-mono)",
            }}
          >
            →
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "min(1200px, 92vw)",
              maxHeight: "82vh",
              cursor: "default",
            }}
          >
            <img
              src={moodboardLargeUrl(activeImage.idx)}
              alt={activeImage.alt || ""}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "82vh",
                objectFit: "contain",
                boxShadow: "0 40px 120px rgba(0,0,0,0.8)",
              }}
            />
            <div
              className="type-meta-sm"
              style={{
                position: "absolute",
                bottom: "-3rem",
                left: 0,
                right: 0,
                color: "var(--fog)",
                textAlign: "center",
                letterSpacing: "var(--ls-meta-wide)",
              }}
            >
              FRAME {activePos + 1} / {canonImages.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
